// LlmProviderPanel — self-contained super-admin panel listing every text /
// image / video LLM provider with a live "ready to use" key-validity badge.
// Lets a super-admin pick the GLOBAL default provider, edit any provider's API
// key (written to the worker .env + live process.env), and shows the SiliconFlow
// account balance. Used on the Automation Pipeline (▶ Run flows) and on the
// Super-Admin Settings page. Loads from the worker via the /worker proxy.
//
// Props:
//   onProvidersLoaded(data)  — optional; called with the raw provider payload
//                              (so a parent, e.g. RunFlows, can drive a per-brand
//                              picker off the same data).
//   onChange()               — optional; called after the global provider changes.

import React, { useCallback, useEffect, useState } from "react";
import { get, post } from "../../api";

export default function LlmProviderPanel({ onProvidersLoaded, onChange }) {
  const [providers, setProviders] = useState({ selected: "siliconflow", text: [], image: [], video: [], balances: {} });
  const [keyDraft, setKeyDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await get("/worker/admin/llm-providers");
      if (r && r.data) { setProviders(r.data); if (onProvidersLoaded) onProvidersLoaded(r.data); }
    } catch (e) { /* non-fatal — worker may be momentarily down */ }
  }, [onProvidersLoaded]);
  useEffect(() => { load(); }, [load]);

  const selectGlobal = async (provider, kind = "text") => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await post("/api/admin/flows/provider", { scope: "global", provider, kind }, false);
      setMsg(`Global ${kind} provider → ${provider === "auto" ? "auto (default order)" : provider}`);
      await load();
      if (onChange) onChange();
    } catch (e) { setErr((e && (e.error || e.message)) || "Set provider failed"); }
    finally { setBusy(false); }
  };
  const saveKey = async (envVar) => {
    const value = (keyDraft[envVar] || "").trim();
    if (!value) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await post("/worker/admin/llm-providers/key", { envVar, value }, false);
      setMsg(`${envVar} saved.`);
      setKeyDraft((d) => ({ ...d, [envVar]: "" }));
      await load();
    } catch (e) { setErr((e && (e.error || e.message)) || "Save key failed"); }
    finally { setBusy(false); }
  };
  const badge = (p) => p.ready
    ? <span className="lpp-chip" style={{ background: "#059669" }} title={p.detail}>● ready to use</span>
    : p.configured
      ? <span className="lpp-chip" style={{ background: "#b45309" }} title={p.detail}>key invalid</span>
      : <span className="lpp-chip" style={{ background: "#9ca3af" }}>no key</span>;

  const keyRow = (p) => (
    <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
      <input className="lpp-field" style={{ flex: 1, fontSize: 11, padding: "2px 6px" }} type="password" autoComplete="off"
        placeholder={`set ${p.apiKeyEnv}…`} value={keyDraft[p.apiKeyEnv] || ""}
        onChange={(e) => { const _v = e.target.value; setKeyDraft((d) => ({ ...d, [p.apiKeyEnv]: _v })); }} />
      <button className="lpp-btn" disabled={busy || !(keyDraft[p.apiKeyEnv] || "").trim()} onClick={() => saveKey(p.apiKeyEnv)}>Save</button>
    </div>
  );

  // A selectable provider group (image or video). selectedId === null → "Auto"
  // (no pin → registry default order). Picking a provider pins it as the lead.
  const providerGroup = (list, kind, selectedId) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      <label className="lpp-prov" style={{ borderColor: !selectedId ? "#111827" : "#e5e7eb", borderWidth: !selectedId ? 2 : 1, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: 600, minWidth: 150, flex: "0 0 auto" }}>
        <input type="radio" name={`lpp-${kind}`} checked={!selectedId} disabled={busy} onChange={() => selectGlobal("auto", kind)} />
        Auto (default order)
      </label>
      {(list || []).map((p) => (
        <div key={p.id} className="lpp-prov" style={{ borderColor: selectedId === p.id ? "#111827" : "#e5e7eb", borderWidth: selectedId === p.id ? 2 : 1 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: 600, margin: 0 }}>
            <input type="radio" name={`lpp-${kind}`} checked={selectedId === p.id} disabled={busy} onChange={() => selectGlobal(p.id, kind)} />
            {p.label} {badge(p)}
          </label>
          <div style={{ fontSize: 10, color: "#94a3b8", margin: "2px 0" }}>{p.model || p.id}</div>
          {keyRow(p)}
        </div>
      ))}
    </div>
  );

  const sf = providers.balances && providers.balances.siliconflow;

  return (
    <div className="lpp">
      <style>{`
        .lpp{font-size:13px}
        .lpp-sec{border:1px solid #e5e7eb;border-radius:10px;padding:12px 14px;background:#fff}
        .lpp-prov{border:1px solid #e5e7eb;border-radius:8px;padding:6px 8px;min-width:200px;flex:1 1 200px;background:#fff}
        .lpp-chip{color:#fff;border-radius:999px;padding:1px 7px;font-size:10px;margin:1px 3px 1px 0;display:inline-block}
        .lpp-field{padding:5px 8px;border:1px solid #d1d5db;border-radius:6px;font-size:12px}
        .lpp-btn{border:1px solid #d1d5db;background:#fff;border-radius:6px;padding:3px 9px;font-size:12px;cursor:pointer}
        .lpp-btn:hover:not(:disabled){background:#111827;color:#fff;border-color:#111827}
        .lpp-lbl{font-size:11px;color:#64748b;margin:2px 0 4px}
      `}</style>
      <div className="lpp-sec">
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, fontWeight: 700, marginBottom: 8 }}>
          <span>🧠 LLM provider</span>
          {sf && (() => {
            const bg = sf.low ? "#dc2626" : sf.chargeEmpty ? "#b45309" : "#059669";
            return (
              <span className="lpp-chip" style={{ background: bg, fontSize: 11 }}
                title={`SiliconFlow ${sf.currency || ""} balance ${sf.totalBalance}` + (sf.chargeEmpty ? " · gift-only (premium video gated; top up to enable)" : "") + (sf.low ? " · LOW — top up at siliconflow.com" : "")}>
                SiliconFlow balance: ${Number(sf.totalBalance).toFixed(2)}{sf.low ? " ⚠ low" : sf.chargeEmpty ? " · gift-only" : ""}
              </span>
            );
          })()}
          <span style={{ fontWeight: 400, color: "#64748b", fontSize: 11 }}>
            pick the global default — flows lead with it; ready providers then the Claude operator are the fallbacks.
          </span>
        </div>
        {err && <div style={{ color: "#b91c1c", fontSize: 12, marginBottom: 6 }}>{err}</div>}
        {msg && <div style={{ color: "#047857", fontSize: 12, marginBottom: 6 }}>{msg}</div>}

        <div className="lpp-lbl">Text / LLM</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {(providers.text || []).map((p) => (
            <div key={p.id} className="lpp-prov" style={{ borderColor: providers.selected === p.id ? "#111827" : "#e5e7eb", borderWidth: providers.selected === p.id ? 2 : 1 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: 600, margin: 0 }}>
                <input type="radio" name="lppGlobalProvider" checked={providers.selected === p.id} disabled={busy} onChange={() => selectGlobal(p.id)} />
                {p.label} {badge(p)}
              </label>
              <div style={{ fontSize: 10, color: "#94a3b8", margin: "2px 0" }}>{p.model || p.id} · {p.country || "—"}</div>
              {keyRow(p)}
            </div>
          ))}
          {(providers.text || []).length === 0 && <span style={{ fontSize: 11, color: "#aaa" }}>loading providers…</span>}
        </div>

        <div className="lpp-lbl">Image provider</div>
        {providerGroup(providers.image, "image", providers.imageSelected)}

        <div className="lpp-lbl" style={{ marginTop: 8 }}>Video provider</div>
        {providerGroup(providers.video, "video", providers.videoSelected)}
      </div>
    </div>
  );
}
